import { Link, createFileRoute } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  CreditCard,
  ExternalLink,
  Loader2,
  Receipt,
} from "lucide-react";
import { useRef, useState } from "react";
import { BackgroundGlow } from "~/components/background-glow";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import {
  getCustomerPortalUrl,
  getUserSubscriptions,
  type UserSubscription,
} from "~/lib/stripe";
import { LoadingSpinner } from "~/routes/__root";

export const Route = createFileRoute("/_authed/dashboard/subscriptions/")({
  pendingComponent: LoadingSpinner,
  pendingMs: 0,
  loader: async () => {
    const [subscriptions, portalUrl] = await Promise.all([
      getUserSubscriptions(),
      getCustomerPortalUrl({
        data: { returnUrl: "/dashboard/subscriptions" },
      }).catch(() => null as string | null),
    ]);

    return { ...subscriptions, portalUrl };
  },
  component: RouteComponent,
});

const BASIC_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_BASIC as
  | string
  | undefined;
const ADVANCED_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ADVANCED as
  | string
  | undefined;

function getPlanName(priceId: string | null) {
  if (priceId && priceId === ADVANCED_PRICE_ID) return "Advanced";
  if (priceId && priceId === BASIC_PRICE_ID) return "Basic";
  return "Custom";
}

function formatMoney(amount: number | null, currency: string | null) {
  if (amount == null || !currency) return null;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  } catch {
    return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getIntervalLabel(interval: UserSubscription["interval"]) {
  switch (interval) {
    case "day":
      return "day";
    case "week":
      return "wk";
    case "month":
      return "mo";
    case "year":
      return "yr";
    default:
      return null;
  }
}

type StatusDisplay = {
  label: string;
  tone: "active" | "warning" | "danger" | "muted" | "info";
};

function getStatusDisplay(subscription: UserSubscription): StatusDisplay {
  if (
    subscription.cancelAtPeriodEnd &&
    (subscription.status === "active" || subscription.status === "trialing")
  ) {
    return { label: "Cancels at period end", tone: "warning" };
  }
  switch (subscription.status) {
    case "active":
      return { label: "Active", tone: "active" };
    case "trialing":
      return { label: "Trial", tone: "info" };
    case "past_due":
      return { label: "Past due", tone: "danger" };
    case "unpaid":
      return { label: "Unpaid", tone: "danger" };
    case "canceled":
      return { label: "Cancelled", tone: "muted" };
    case "incomplete":
      return { label: "Incomplete", tone: "muted" };
    case "incomplete_expired":
      return { label: "Expired", tone: "muted" };
    case "paused":
      return { label: "Paused", tone: "muted" };
    default:
      return { label: subscription.status, tone: "muted" };
  }
}

const TONE_CLASSES: Record<StatusDisplay["tone"], string> = {
  active:
    "border-emerald-500/30 bg-emerald-500/10 text-emerald-300 [&_.dot]:bg-emerald-400",
  warning:
    "border-amber-500/30 bg-amber-500/10 text-amber-300 [&_.dot]:bg-amber-400",
  danger:
    "border-rose-500/30 bg-rose-500/10 text-rose-300 [&_.dot]:bg-rose-400",
  info: "border-sky-500/30 bg-sky-500/10 text-sky-300 [&_.dot]:bg-sky-400",
  muted:
    "border-border bg-secondary/40 text-muted-foreground [&_.dot]:bg-muted-foreground/70",
};

function StatusBadge({ subscription }: { subscription: UserSubscription }) {
  const status = getStatusDisplay(subscription);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${TONE_CLASSES[status.tone]}`}
    >
      <span
        className={`dot h-1.5 w-1.5 rounded-full ${
          status.tone === "active" ? "animate-pulse" : ""
        }`}
      />
      {status.label}
    </span>
  );
}

function PlanBadge({ priceId }: { priceId: string | null }) {
  const name = getPlanName(priceId);
  const isAdvanced = name === "Advanced";
  return (
    <Badge
      variant={isAdvanced ? "default" : "secondary"}
      className={
        isAdvanced
          ? "bg-primary/15 text-primary border border-primary/30 hover:bg-primary/15"
          : "bg-secondary/70 text-foreground border border-border"
      }
    >
      {name}
    </Badge>
  );
}

function NextPaymentCell({ subscription }: { subscription: UserSubscription }) {
  const amount = formatMoney(subscription.unitAmount, subscription.currency);
  const interval = getIntervalLabel(subscription.interval);
  const amountWithInterval =
    amount && interval ? `${amount}/${interval}` : amount;

  if (subscription.status === "canceled") {
    return (
      <div>
        <div className="text-muted-foreground">No upcoming payment</div>
        {formatDate(subscription.endDate) && (
          <div className="text-[11px] text-muted-foreground/70">
            Ended {formatDate(subscription.endDate)}
          </div>
        )}
      </div>
    );
  }

  if (
    subscription.status === "incomplete" ||
    subscription.status === "incomplete_expired" ||
    subscription.status === "paused"
  ) {
    return <span className="text-muted-foreground">—</span>;
  }

  if (subscription.cancelAtPeriodEnd && subscription.endDate) {
    return (
      <div>
        <div className="font-medium text-amber-300">
          Ends {formatDate(subscription.endDate)}
        </div>
        <div className="text-[11px] text-muted-foreground">
          No further charges
        </div>
      </div>
    );
  }

  if (subscription.status === "past_due") {
    return (
      <div>
        <div className="font-medium text-rose-300">Payment failed</div>
        {amountWithInterval && (
          <div className="text-[11px] text-muted-foreground tabular-nums">
            {amountWithInterval}
          </div>
        )}
      </div>
    );
  }

  const periodEnd = formatDate(subscription.currentPeriodEnd);
  return (
    <div>
      <div className="font-medium text-foreground">{periodEnd ?? "—"}</div>
      {amountWithInterval && (
        <div className="text-[11px] text-muted-foreground tabular-nums">
          {amountWithInterval}
        </div>
      )}
    </div>
  );
}

function ManageInlineLink({
  portalUrl,
  onError,
}: {
  portalUrl: string | null;
  onError: (msg: string) => void;
}) {
  const [isLoading, setIsLoading] = useState(false);

  async function handleClick(event: React.MouseEvent<HTMLAnchorElement>) {
    if (portalUrl) return; // let the anchor navigate natively
    event.preventDefault();
    setIsLoading(true);
    try {
      const url = await getCustomerPortalUrl({
        data: { returnUrl: "/dashboard/subscriptions" },
      });
      window.location.assign(url);
    } catch (cause) {
      onError(
        cause instanceof Error
          ? cause.message
          : "Failed to open the Stripe billing dashboard",
      );
      setIsLoading(false);
    }
  }

  return (
    <a
      href={portalUrl ?? "#"}
      onClick={handleClick}
      aria-disabled={isLoading || undefined}
      className="inline-flex items-center gap-1 font-semibold text-primary underline-offset-4 hover:underline aria-disabled:cursor-not-allowed aria-disabled:opacity-60"
    >
      Manage in Stripe
      {isLoading ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <ExternalLink className="h-3 w-3" />
      )}
    </a>
  );
}

function SubscriptionRow({ subscription }: { subscription: UserSubscription }) {
  const guildName =
    subscription.metadata.guild_name?.trim() || "Unknown server";
  const guildId = subscription.metadata.guild_id?.trim() || null;
  const startDate = formatDate(subscription.startDate);

  return (
    <TableRow>
      <TableCell className="max-w-64 whitespace-normal wrap-break-word px-6 py-4">
        {guildId ? (
          <Link
            to="/dashboard/$guildId"
            params={{ guildId }}
            className="block max-w-full whitespace-normal wrap-break-word font-semibold text-foreground hover:text-primary hover:underline underline-offset-4"
          >
            {guildName}
          </Link>
        ) : (
          <div className="max-w-full whitespace-normal wrap-break-word font-semibold text-foreground">
            {guildName}
          </div>
        )}
      </TableCell>
      <TableCell className="px-6 py-4">
        <PlanBadge priceId={subscription.priceId} />
      </TableCell>
      <TableCell className="px-6 py-4">
        <StatusBadge subscription={subscription} />
      </TableCell>
      <TableCell className="px-6 py-4">
        <NextPaymentCell subscription={subscription} />
      </TableCell>
      <TableCell className="px-6 py-4 text-muted-foreground">
        {startDate ?? "—"}
      </TableCell>
    </TableRow>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card/30 py-24 text-center shadow-xl shadow-primary/5 backdrop-blur-md backdrop-saturate-150">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-secondary/60">
        <Receipt className="h-6 w-6 text-muted-foreground" />
      </div>
      <p className="font-semibold text-foreground">No subscriptions yet</p>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        Once you subscribe Overseer to a server, you'll be able to manage it
        here.
      </p>
    </div>
  );
}

type PageData = {
  hasMore: boolean;
  subscriptions: UserSubscription[];
};

const INITIAL_CURSOR_KEY = "";

function cursorToKey(cursor: string | null) {
  return cursor ?? INITIAL_CURSOR_KEY;
}

function RouteComponent() {
  const initial = Route.useLoaderData();
  const initialPage: PageData = {
    hasMore: initial.hasMore,
    subscriptions: initial.subscriptions,
  };
  const [data, setData] = useState<PageData>(initialPage);
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [paginatingDirection, setPaginatingDirection] = useState<
    "next" | "prev" | null
  >(null);
  const [error, setError] = useState<string | null>(null);

  const pagesCacheRef = useRef<Map<string, PageData>>(
    new Map([[INITIAL_CURSOR_KEY, initialPage]]),
  );
  const inflightPrefetchesRef = useRef<Map<string, Promise<PageData>>>(
    new Map(),
  );

  const isPaginating = paginatingDirection !== null;
  const pageNumber = cursorStack.length;
  const hasPrevPage = cursorStack.length > 1;
  const hasNextPage = data.hasMore;
  const canGoPrev = hasPrevPage && !isPaginating;
  const canGoNext = hasNextPage && !isPaginating;

  async function fetchPage(cursor: string | null): Promise<PageData> {
    const key = cursorToKey(cursor);
    const cached = pagesCacheRef.current.get(key);
    if (cached) return cached;

    const existing = inflightPrefetchesRef.current.get(key);
    if (existing) return existing;

    const promise = getUserSubscriptions(
      cursor ? { data: { startingAfter: cursor } } : undefined,
    )
      .then((fresh) => {
        pagesCacheRef.current.set(key, fresh);
        return fresh;
      })
      .finally(() => {
        inflightPrefetchesRef.current.delete(key);
      });

    inflightPrefetchesRef.current.set(key, promise);
    return promise;
  }

  function prefetchNext() {
    if (!hasNextPage) return;
    const lastId =
      data.subscriptions[data.subscriptions.length - 1]?.id ?? null;
    if (!lastId) return;
    if (pagesCacheRef.current.has(cursorToKey(lastId))) return;
    void fetchPage(lastId).catch(() => {
      /* swallow prefetch errors */
    });
  }

  function prefetchPrev() {
    if (!hasPrevPage) return;
    const prevCursor = cursorStack[cursorStack.length - 2] ?? null;
    if (pagesCacheRef.current.has(cursorToKey(prevCursor))) return;
    void fetchPage(prevCursor).catch(() => {
      /* swallow prefetch errors */
    });
  }

  async function handleNext() {
    if (!canGoNext) return;
    const lastId =
      data.subscriptions[data.subscriptions.length - 1]?.id ?? null;
    if (!lastId) return;
    setError(null);
    const cached = pagesCacheRef.current.get(cursorToKey(lastId));
    if (cached) {
      setCursorStack((prev) => [...prev, lastId]);
      setData(cached);
      return;
    }
    setPaginatingDirection("next");
    try {
      const next = await fetchPage(lastId);
      setCursorStack((prev) => [...prev, lastId]);
      setData(next);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Failed to load next page",
      );
    } finally {
      setPaginatingDirection(null);
    }
  }

  async function handlePrev() {
    if (!canGoPrev) return;
    setError(null);
    const newStack = cursorStack.slice(0, -1);
    const cursor = newStack[newStack.length - 1] ?? null;
    const cached = pagesCacheRef.current.get(cursorToKey(cursor));
    if (cached) {
      setCursorStack(newStack);
      setData(cached);
      return;
    }
    setPaginatingDirection("prev");
    try {
      const prev = await fetchPage(cursor);
      setCursorStack(newStack);
      setData(prev);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Failed to load previous page",
      );
    } finally {
      setPaginatingDirection(null);
    }
  }

  const showPagination = hasPrevPage || hasNextPage;

  return (
    <div className="min-h-screen">
      <BackgroundGlow />

      <div className="relative mx-auto max-w-6xl px-6 py-16">
        <div className="mb-10">
          <p className="mb-1 text-xs font-semibold uppercase tracking-widest text-primary">
            Billing
          </p>
          <h1 className="text-3xl font-extrabold tracking-tight text-foreground">
            Your Subscriptions
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            View, update, and cancel your Overseer subscriptions.
            {data.subscriptions.length > 0 && (
              <>
                {" "}
                <ManageInlineLink
                  portalUrl={initial.portalUrl}
                  onError={setError}
                />
              </>
            )}
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
            {error}
          </div>
        )}

        {data.subscriptions.length === 0 && cursorStack.length === 1 ? (
          <EmptyState />
        ) : (
          <div className="overflow-hidden rounded-2xl border border-border bg-card/30 shadow-xl shadow-primary/5 backdrop-blur-md backdrop-saturate-150">
            <Table className="text-sm">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="px-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Server
                  </TableHead>
                  <TableHead className="px-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Plan
                  </TableHead>
                  <TableHead className="px-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Status
                  </TableHead>
                  <TableHead className="px-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Next payment
                  </TableHead>
                  <TableHead className="px-6 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Started
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.subscriptions.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={5}
                      className="py-12 text-center text-sm text-muted-foreground"
                    >
                      No subscriptions on this page.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.subscriptions.map((subscription) => (
                    <SubscriptionRow
                      key={subscription.id}
                      subscription={subscription}
                    />
                  ))
                )}
              </TableBody>
            </Table>

            {showPagination && (
              <div className="flex items-center justify-between gap-4 border-t border-border px-6 py-4">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <CreditCard className="h-3.5 w-3.5" />
                  Page {pageNumber}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrev}
                    onMouseEnter={prefetchPrev}
                    onFocus={prefetchPrev}
                    disabled={!canGoPrev}
                    className="gap-1.5"
                  >
                    {paginatingDirection === "prev" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ChevronLeft className="h-3.5 w-3.5" />
                    )}
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNext}
                    onMouseEnter={prefetchNext}
                    onFocus={prefetchNext}
                    disabled={!canGoNext}
                    className="gap-1.5"
                  >
                    Next
                    {paginatingDirection === "next" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
